import { PopoverPlacement } from "@idui/react-popover";
import { AddLocationAlt, Edit as EditIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { startCase } from "lodash";
import { FC, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  AddStationWrapperStyled,
  DropdownTemplate,
  FramerTooltip,
} from "@ryde/controls";
import { useCourseStationActions } from "@ryde/redux";
import {
  dropdownStationListAtom,
  errors,
  ICourseDto,
  IDropdownItem,
  Log,
  simpleResultErrorInfo,
  SINGLE_KEY,
  useDrawers,
} from "@ryde/shared";

interface ICourseAddStation {
  courseDto: ICourseDto;
}

interface INewCourseStationFormModel {
  stationAddId?: number;
}

/* TODO: adding station without passengers will be enabled when fixed rides will be enabled for adding */
export const CourseAddStation: FC<ICourseAddStation> = ({ courseDto }) => {
  const methods = useForm<INewCourseStationFormModel>({
    defaultValues: { stationAddId: undefined },
  });
  const { t } = useTranslation();
  const { getValues, setValue, watch } = methods;

  const stationsDropdownList = useRecoilValue(dropdownStationListAtom);
  const { openEditFormDrawer } = useDrawers();

  const { courseAddStationAction, fetchDropdownData } =
    useCourseStationActions();
  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  const excludeStationIds = useMemo(() => {
    return courseDto.metadata?.stationIds;
  }, [courseDto]);

  const coursesDropdownListFiltered: IDropdownItem[] = useMemo(() => {
    const excludeStationIdsSet = excludeStationIds
      ? new Set(excludeStationIds)
      : null;
    const stations = stationsDropdownList?.filter(
      x => !excludeStationIdsSet?.has(+x.value),
    );
    return stations || [];
  }, [excludeStationIds, stationsDropdownList]);

  const selectStationFirst = t("ride.PLEASE_CHOOSE_STATION_FIRST");
  const manageStation = t("general.MANAGE_ENTITY", {
    entity: t(`station.${SINGLE_KEY}`),
  });

  const handleStationAdd = useEvent(async () => {
    const stationIdValue = getValues("stationAddId");

    if (!stationIdValue || !+stationIdValue) {
      Log.warnAndToast(selectStationFirst);
      return;
    }

    Log.debug(
      `Adding station to course ${courseDto.id} with station id: ${stationIdValue}`,
    );
    const stationAddedResult = await courseAddStationAction(
      courseDto,
      +stationIdValue,
    );

    if (stationAddedResult.success) {
      Log.debug("Station added successfully");
      setValue("stationAddId", undefined);
    } else {
      Log.warnAndToastDetailed(
        t(errors.ride.stationNotAdded),
        simpleResultErrorInfo(stationAddedResult, t),
      );
    }
  });

  const watchedStationId: number | undefined = watch("stationAddId");

  const openStationCard = useEvent(() => {
    if (!watchedStationId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: watchedStationId,
    });
  });

  return (
    <FormProvider {...methods}>
      <AddStationWrapperStyled>
        <DropdownTemplate
          name="stationAddId"
          size="small"
          formAttributes={{
            selectItems: coursesDropdownListFiltered,
            disabled: !coursesDropdownListFiltered?.length,
            skipDropdownItemsTranslation: true,
          }}
        />
        <FramerTooltip
          content={
            !watchedStationId
              ? selectStationFirst
              : t("general.ADD_NEW", { entity: t(`station.${SINGLE_KEY}`) })
          }
          placement={PopoverPlacement.top}
        >
          {/* Prevent "A disabled element does not fire events." MUI error*/}
          <span>
            <IconButton
              disabled={!watchedStationId}
              onClick={handleStationAdd}
              color="primary"
            >
              <AddLocationAlt />
            </IconButton>
          </span>
        </FramerTooltip>

        <FramerTooltip
          content={
            !watchedStationId ? selectStationFirst : startCase(manageStation)
          }
          placement={PopoverPlacement.top}
        >
          {/* Prevent "A disabled element does not fire events." MUI error*/}
          <span>
            <IconButton
              disabled={!watchedStationId}
              onClick={openStationCard}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </span>
        </FramerTooltip>
      </AddStationWrapperStyled>
    </FormProvider>
  );
};
